import useOverlay from "@/hooks/common/useOverlay";
import {onClickSSO} from "@COMPONENTS/common/commonUtil";
import SharePopup from "@COMPONENTS/common/modal/share/Mobile";
import withWishList from "@COMPONENTS/hoc/withWishList";
import Category from "@COMPONENTS/layout/header/Mobile/Category";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {
	BtnBack,
	BtnCategory,
	BtnGroup,
	BtnLike,
	BtnMain,
	BtnShare,
	HeaderWrap,
	Wrap,
} from "@FRAMEWORK/ui/mobile/header/NewHeader";
import {Accessibility} from "@FRAMEWORK/ui/mobile/text";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import useGlobalMenu from "@QUERIES/bff/global/useGlobalMenu";
import useWishlist from "@QUERIES/essential/customer/useWishlist";
import {useRouter} from "next/router";
import {memo} from "react";

interface HeaderProps {
	/** 상단 고정 여부
	 * @description 페이지마다 상단 고정 여부 확인하여 사용
	 */
	notFixed?: boolean;
	/** 메뉴 카테고리 버튼 여부
	 */
	category?: boolean;
	/** true: 메뉴(햄버거) 사용여부, false: 뒤로가기 버튼 */
	gnb?: boolean;
	/** 타이틀 */
	title?: string;
	/**
	 * tool
	 * @description 헤더에 배치할 도구 순서
	 */
	tools?: Array<"search" | "mypage" | "wish" | "share" | "home">;
	/**
	 * 페이지 프로퍼티
	 * @description 찜한 상품 사용
	 */
	pageProps?: {
		goodsCd?: string;
		evCd?: string;
		menu?: string;
		dspSid?: string;
	};
	/**
	 * 좋아요 표시 기준값
	 * @description 찜한 상품 사용
	 * @description event (행사관련) | goods (상품관련)
	 * @description default event
	 */
	// likeType?: "event" | "goods";

	/**
	 * 찜한 상품 추가시 callback 이벤트
	 */
	wishAddConfirm?: (e: any) => void;
	/**
	 * 찜한 상품 삭제시 callback 이벤트
	 */
	wishRemoveConfirm?: (e: any) => void;
}

const WishList = withWishList(props => (
	<BtnLike type="button" icon="wishList" {...props}>
		<span>찜한 상품</span>
	</BtnLike>
));

const Tool = ({
	tools,
	pageProps: {evCd = "", dspSid = "", menu = ""},
	wishAddConfirm,
	wishRemoveConfirm,
}: Pick<HeaderProps, "tools" | "pageProps" | "wishAddConfirm" | "wishRemoveConfirm">) => {
	/** 로그인 */
	const signin = useCustomerMe();
	const webCustNo = signin.data?.body.webCustNo || "";

	/** 찜한 상품 */
	const useWish = useWishlist(webCustNo, {enabled: !!webCustNo, throwOnError: false});
	const wishlist = useWish.data?.body?.wishProductsResultList || [];

	const [open] = useOverlay();

	const share = () => {
		open(overlay => (
			<SharePopup {...overlay} baseUrl={`${location.origin}${location.pathname}`} parameter={location.search} />
		));
	};

	const isWishNo = wishlist.filter(wish => wish.evCd === evCd)?.at(0)?.productWishId || "";

	return (
		<BtnGroup>
			{tools.map((tool, i) => {
				if (tool === "home")
					return (
						<BtnMain
							href={publicRuntimeConfig.ENV_PORTAL_URL}
							key={i}
							onClick={e => onClickSSO(e, publicRuntimeConfig.ENV_PORTAL_URL)}
						>
							<Accessibility>메인 페이지로 이동</Accessibility>
						</BtnMain>
					);
				else if (tool === "wish")
					return (
						<WishList
							key={i}
							isActive={!!isWishNo}
							wishNo={isWishNo}
							diviCd="E"
							evCd={evCd}
							addConfirm={wishAddConfirm}
							removeConfirm={wishRemoveConfirm}
							link={`/product/detailPackage?menu=${menu}&dspSid=${dspSid}&evCd=${evCd}`}
						/>
					);
				else if (tool === "share")
					return (
						<BtnShare type="button" key={i} onClick={share}>
							<span>공유하기</span>
						</BtnShare>
					);
				else {
					return <></>;
				}
			})}
		</BtnGroup>
	);
};

const CategoryList = () => {
	const [open] = useOverlay();
	const router = useRouter();

	/** 메뉴 관련 */
	const globalMenu = useGlobalMenu()?.data?.body || null;
	const menuLv3List = globalMenu?.menuLv3List || [];

	/** dspSid Lv3 */
	const currentDspSid = (router.query?.dspSid as string) || "";
	const dspNmLv3 = menuLv3List.find(o => o.dspSid === currentDspSid && o.mobileYn === "Y")?.dspNm || "";

	return (
		<BtnCategory type="button" onClick={() => open(props => <Category {...props} />)}>
			<h2>{dspNmLv3}</h2>
		</BtnCategory>
	);
};

const Header = (props: HeaderProps) => {
	const {notFixed = false, category, title, tools = [], wishAddConfirm, wishRemoveConfirm} = props;
	return (
		<>
			<HeaderWrap $isNotFixed={notFixed}>
				<Wrap>
					<BtnBack type="button" onClick={() => history.back()}>
						<span>뒤로가기</span>
					</BtnBack>
					{category ? <CategoryList /> : <h2>{title}</h2>}
					<Tool
						tools={tools}
						pageProps={props.pageProps || {}}
						wishAddConfirm={wishAddConfirm}
						wishRemoveConfirm={wishRemoveConfirm}
					/>
				</Wrap>
			</HeaderWrap>
		</>
	);
};

export default memo(Header);
